import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Copy, FileText, Target } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const CopyEventModal = ({ event, onClose }) => {
  const navigate = useNavigate();
  const [copying, setCopying] = useState(false);

  const handleCopyDataOnly = () => {
    setCopying(true);
    
    // Prepare event data without goals
    const eventData = {
      title: `${event.title} (Copy)`,
      description: event.description,
      date: '',
      time: event.time || '12:00',
      location: event.location,
      expected_guests: event.expected_guests,
      invited_by: event.invited_by,
      blessings: event.blessings,
      event_code_prefix: event.event_code_prefix,
      upi_ids: event.upi_ids || [],
      event_photos: [], // Don't copy photos
      contribution_goals: [] // Empty goals
    };

    // Store in sessionStorage to prefill create page
    sessionStorage.setItem('copyEventData', JSON.stringify(eventData));
    
    // Navigate to create event page
    navigate('/create-event');
  };

  const handleCopyDataAndGoals = () => {
    setCopying(true);
    
    // Prepare event data with goals (but reset amounts)
    const copiedGoals = event.contribution_goals.map(goal => ({
      title: goal.title,
      description: goal.description,
      target_amount: '', // Reset amount
      additional_info: goal.additional_info,
      minimum_contribution: goal.minimum_contribution
    }));

    const eventData = {
      title: `${event.title} (Copy)`,
      description: event.description,
      date: '',
      time: event.time || '12:00',
      location: event.location,
      expected_guests: event.expected_guests,
      invited_by: event.invited_by,
      blessings: event.blessings,
      event_code_prefix: event.event_code_prefix,
      upi_ids: event.upi_ids || [],
      event_photos: [], // Don't copy photos
      contribution_goals: copiedGoals // Copy goals structure
    };

    // Store in sessionStorage to prefill create page
    sessionStorage.setItem('copyEventData', JSON.stringify(eventData));
    
    // Navigate to create event page
    navigate('/create-event');
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-lg md:text-2xl font-bold text-center">
            Copy Event
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 md:space-y-4 py-2 md:py-4">
          <p className="text-xs md:text-sm text-gray-600 text-center">
            Choose how you want to copy "<span className="font-semibold">{event.title}</span>"
          </p>

          {/* Option 1: Copy Data Only */}
          <Button
            variant="outline"
            className="w-full h-auto p-3 md:p-6 border-2 border-blue-500 hover:bg-blue-50"
            onClick={handleCopyDataOnly}
            disabled={copying}
          >
            <div className="flex flex-col items-start w-full">
              <div className="flex items-center mb-1 md:mb-2">
                <FileText className="h-4 w-4 md:h-6 md:w-6 text-blue-600 mr-1 md:mr-2" />
                <span className="font-semibold text-sm md:text-lg text-blue-600">
                  Copy Data Only
                </span>
              </div>
              <p className="text-xs text-gray-600 text-left">
                Copies event details (title, description, location, UPI IDs) without contribution goals. 
                You'll start with empty goals.
              </p>
              <div className="mt-2 md:mt-3 text-xs text-gray-500">
                <strong>Includes:</strong> Title, Description, Location, Time, UPI IDs, Expected Guests
              </div>
              <div className="text-xs text-gray-500">
                <strong>Excludes:</strong> Contribution Goals, Photos, Amounts
              </div>
            </div>
          </Button>

          {/* Option 2: Copy Data & Goals */}
          <Button
            variant="outline"
            className="w-full h-auto p-3 md:p-6 border-2 border-green-500 hover:bg-green-50"
            onClick={handleCopyDataAndGoals}
            disabled={copying}
          >
            <div className="flex flex-col items-start w-full">
              <div className="flex items-center mb-1 md:mb-2">
                <Target className="h-4 w-4 md:h-6 md:w-6 text-green-600 mr-1 md:mr-2" />
                <span className="font-semibold text-sm md:text-lg text-green-600">
                  Copy Data & Goals
                </span>
              </div>
              <p className="text-xs text-gray-600 text-left">
                Copies event details AND contribution goals structure. 
                Goal amounts will be reset so you can enter new targets.
              </p>
              <div className="mt-2 md:mt-3 text-xs text-gray-500">
                <strong>Includes:</strong> Everything + Goal Titles, Descriptions, Settings
              </div>
              <div className="text-xs text-gray-500">
                <strong>Excludes:</strong> Goal Amounts (reset to empty), Photos
              </div>
            </div>
          </Button>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2 md:p-3">
            <p className="text-xs text-yellow-800">
              <strong>📝 Note:</strong> A new unique event will be created. 
              You'll be able to edit all fields including goals before publishing.
            </p>
          </div>

          <Button
            variant="ghost"
            className="w-full text-xs md:text-sm"
            onClick={onClose}
          >
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CopyEventModal;
